/**
 * 行为埋点统一封装（主应用 / 键鼠子应用共用）
 * 上报：POST /api/v1/event-logs（失败静默）
 */
import { getActiveBrandId } from "./brandDeviceFilter";

export type TrackAppType =
  | "keyboard"
  | "mouse"
  | "earphone"
  | "hub-homepage"
  | "portal";

export type TrackClickPayload = {
  moduleId: string;
  elementId: string;
  appType?: TrackAppType;
  deviceType?: string;
  pagePath?: string;
  extra?: Record<string, unknown>;
};

export type TrackVisitPayload = {
  moduleId: string;
  pagePath?: string;
  elementId?: string;
  appType?: TrackAppType;
  deviceType?: string;
  extra?: Record<string, unknown>;
};

const ANONYMOUS_KEY = "EVENT_LOG_ANONYMOUS_ID";
const SESSION_KEY = "EVENT_LOG_SESSION_ID";

const createRandomId = () => {
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const getOrCreateStorageId = (key: string) => {
  if (typeof localStorage === "undefined") return createRandomId();
  const existing = localStorage.getItem(key)?.trim();
  if (existing) return existing;
  const value = createRandomId();
  localStorage.setItem(key, value);
  return value;
};

export const getTrackVisitorIdentity = () => {
  let userId: number | undefined;
  try {
    const raw =
      typeof localStorage !== "undefined"
        ? localStorage.getItem("userInfo")
        : null;
    const userInfo = raw ? JSON.parse(raw) : null;
    const idVal = userInfo?.id;
    if (typeof idVal === "number" && Number.isFinite(idVal)) userId = idVal;
    else if (typeof idVal === "string" && idVal.trim() !== "") {
      const parsed = Number(idVal);
      if (Number.isFinite(parsed)) userId = parsed;
    }
  } catch {
    // ignore
  }
  return {
    anonymousId: getOrCreateStorageId(ANONYMOUS_KEY),
    sessionId: getOrCreateStorageId(SESSION_KEY),
    userId,
  };
};

/** 与推广卡 / 更新日志一致：EWEADN | EXX（后端会 normalize 小写） */
export function resolveEventBrandCode(): string {
  const id = getActiveBrandId().trim().toLowerCase();
  return id === "exx" ? "EXX" : "EWEADN";
}

/** 当前是否处于演示模式（与 localStorage.isDemoMode 一致） */
export function isDemoModeActive(): boolean {
  try {
    return (
      typeof localStorage !== "undefined" &&
      localStorage.getItem("isDemoMode") === "true"
    );
  } catch {
    return false;
  }
}

/** 合并演示标识与 isElectron，供 logEvent / logRouteExposure 等复用 */
export function mergeTrackExtra(
  extra?: Record<string, unknown>
): Record<string, unknown> {
  const fromStorage = isDemoModeActive();
  const fromExtra =
    extra?.isDemoMode === true ||
    extra?.isDemoMode === 1 ||
    extra?.isDemoMode === "true" ||
    extra?.isDemoMode === "1" ||
    extra?.entryMode === "demo";
  const isDemoMode = fromStorage || fromExtra;
  const merged = {
    ...extra,
    isDemoMode,
    entryMode: isDemoMode ? "demo" : "real",
    isElectron:
      typeof navigator !== "undefined" && /electron/i.test(navigator.userAgent),
  };
  // 后端 demoMode=only 依赖 JSON $.isDemoMode；确保始终写入 boolean
  merged.isDemoMode = isDemoMode;
  return merged;
}

function inferAppType(moduleId: string): TrackAppType {
  if (moduleId.startsWith("keyboard-management")) return "keyboard";
  if (moduleId.startsWith("mouse-management")) return "mouse";
  if (moduleId.startsWith("earphone-management")) return "earphone";
  return "hub-homepage";
}

function inferDeviceType(
  appType: TrackAppType,
  explicit?: string
): string | undefined {
  if (explicit) return explicit;
  if (appType === "keyboard") return "keyboard";
  if (appType === "mouse") return "mouse";
  if (appType === "earphone") return "earphone";
  return undefined;
}

function postEventLog(body: Record<string, unknown>): void {
  if (typeof fetch === "undefined") return;
  void fetch("/api/v1/event-logs", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  }).catch(() => {});
}

function buildEventBody(
  eventType: "VISIT" | "CLICK",
  payload: {
    moduleId: string;
    elementId?: string;
    appType?: TrackAppType;
    deviceType?: string;
    pagePath?: string;
    extra?: Record<string, unknown>;
  }
): Record<string, unknown> {
  const appType = payload.appType ?? inferAppType(payload.moduleId);
  const pagePath =
    payload.pagePath ??
    (typeof window !== "undefined" ? window.location.pathname : "");
  const identity = getTrackVisitorIdentity();
  return {
    eventType,
    elementId: payload.elementId,
    appType,
    deviceType: inferDeviceType(appType, payload.deviceType),
    moduleId: payload.moduleId,
    pagePath,
    brand: resolveEventBrandCode(),
    ...identity,
    referer: typeof document !== "undefined" ? document.referrer : "",
    extra: JSON.stringify(mergeTrackExtra(payload.extra)),
  };
}

/** CLICK 行为埋点 */
export function trackClick(payload: TrackClickPayload): void {
  try {
    postEventLog(buildEventBody("CLICK", payload));
  } catch {
    // ignore
  }
}

/** VISIT 行为埋点（页内 Tab 等；路由级仍可用 logRouteExposure） */
export function trackVisit(payload: TrackVisitPayload): void {
  try {
    postEventLog(
      buildEventBody("VISIT", {
        ...payload,
        elementId: payload.elementId ?? `visit:${payload.moduleId}`,
      })
    );
  } catch {
    // ignore
  }
}
